import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Users,
  UserCheck,
  Vote,
  Calendar,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Rocket,
  Shield,
  Clock,
  Zap,
} from 'lucide-react';
import CollapsibleSection from './CollapsibleSection';

export function DeploymentPathwaySection({ cityName }: { cityName: string }) {
  const [activeVariant, setActiveVariant] = useState<'sufficient' | 'minimal'>('sufficient');

  return (
    <section
      id="deployment-pathway"
      className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-purple-50 to-background"
    >
      <div className="container px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="space-y-8">
            {/* Section Header */}
            <div className="text-center space-y-4">
              <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                From Champions to Community-Led Governance
              </h2>
              <p className="text-lg sm:text-xl text-muted-foreground max-w-4xl mx-auto">
                Here's exactly how we transition from a handful of champions to a fully elected,
                community-run governance structure.
              </p>
            </div>

            {/* Reality Check Banner */}
            <Card className="border-2 border-amber-200 bg-amber-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-amber-600 mt-1 flex-shrink-0" />
                  <div className="space-y-2">
                    <h3 className="font-bold text-amber-900 text-lg">
                      Important Context: You're Building Something New
                    </h3>
                    <p className="text-sm text-amber-800 leading-relaxed">
                      <strong>99.99% of cities don't have this.</strong> When we say "organizing
                      your existing committees," we recognize that most community-level governance
                      structures don't exist yet. Unlike city government (mayor, council,
                      departments), there's typically no citywide{' '}
                      <em>resident governance framework</em>. That's what we're building together. a
                      digital infrastructure that enables residents to self-organize, make
                      collective decisions, and pool resources without waiting on politicians.
                    </p>
                    <p className="text-sm text-amber-800 leading-relaxed">
                      <strong>
                        You're not replacing anything. you're creating capacity that never existed.
                      </strong>{' '}
                      The governance framework you saw above isn't something {cityName} residents
                      currently have. It's the structure you'll build together using our platform.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* The Deployment Process - 4 Phases */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold text-center">The 4-Phase Deployment Process</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Phase 1: Find Champions */}
                <Card className="border-l-4 border-l-blue-500">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-sm">
                        1
                      </div>
                      Find Champions
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      We recruit 5-20 {cityName} residents who care about community governance.
                    </p>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span>Champions give us feedback on what the community needs</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span>We build features based on their input</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span>They test the platform and provide revisions</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                {/* Phase 2: Assign Temporary Roles */}
                <Card className="border-l-4 border-l-purple-500">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <div className="w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold text-sm">
                        2
                      </div>
                      Assign Temporary Roles
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      Champions who demonstrate leadership capacity are assigned{' '}
                      <strong>temporary</strong> governance roles.
                    </p>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-2">
                      <p className="text-xs font-semibold text-purple-900">
                        Minimum Required for Launch:
                      </p>
                      <ul className="space-y-1 text-xs text-purple-800">
                        <li>• 1 Community Elect (governance expert)</li>
                        <li>• 1-3 Community Directors</li>
                        <li>
                          • Ideally: Directors in key departments (Finance, Governance, Disputes,
                          Group Purchasing, Community Engagement)
                        </li>
                      </ul>
                    </div>
                    <p className="text-xs text-muted-foreground italic">
                      These roles are temporary. champions fill them to get the system running, but
                      the community will elect permanent leaders later.
                    </p>
                  </CardContent>
                </Card>

                {/* Phase 3: Launch to Community */}
                <Card className="border-l-4 border-l-green-500">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-sm">
                        3
                      </div>
                      Launch to Community
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      The app goes live. Residents sign up, explore features, and start using the
                      platform.
                    </p>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <Rocket className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>300, 3,000, or 10,000+ residents join the platform</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Shield className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>
                          Temporary champions manage operations while community gets familiar
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Users className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>
                          Residents learn how departments work, what roles exist, and how governance
                          operates
                        </span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                {/* Phase 4: Elections */}
                <Card className="border-l-4 border-l-amber-500">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold text-sm">
                        4
                      </div>
                      Community Elections
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      After the community has time to learn the system, we open up elections for{' '}
                      <strong>all positions</strong>.
                    </p>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                      <p className="text-xs font-semibold text-amber-900">
                        3-Stage Election Process:
                      </p>
                      <div className="space-y-2 text-xs text-amber-800">
                        <div>
                          <strong>Stage 1: Candidate Registration</strong>
                          <p>
                            Anyone can declare candidacy for any open position (Community Elect,
                            Community Directors, Department Elect, Department Directors, Team Lead)
                          </p>
                        </div>
                        <div>
                          <strong>Stage 2: Primary Vote (Top 3 Advance)</strong>
                          <p>
                            Community votes to narrow candidates down to top 3 finalists per
                            position
                          </p>
                        </div>
                        <div>
                          <strong>Stage 3: Final Election (Popular Vote)</strong>
                          <p>Top 3 candidates compete, winner takes the position</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Two Deployment Variants */}
            <CollapsibleSection
              title={
                <>
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                  Two Deployment Scenarios
                </>
              }
              subtitle={
                <span>
                  The timeline depends on how many qualified champions we find during the testing
                  phase.
                </span>
              }
              className="border-2 border-purple-200"
            >
              <Tabs
                value={activeVariant}
                onValueChange={(val) => setActiveVariant(val as 'sufficient' | 'minimal')}
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="sufficient" className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    <span className="hidden sm:inline">Sufficient Champions</span>
                    <span className="sm:hidden">Variant 1</span>
                  </TabsTrigger>
                  <TabsTrigger value="minimal" className="flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    <span className="hidden sm:inline">Minimal Champions</span>
                    <span className="sm:hidden">Variant 2</span>
                  </TabsTrigger>
                </TabsList>

                {/* Variant 1: Sufficient Champions */}
                <TabsContent value="sufficient" className="space-y-4 mt-6">
                  {/* Variant 1 Timeline Summary - Shows at top when this tab is active */}
                  <div className="bg-gradient-to-r from-green-100 to-green-50 border-2 border-green-300 rounded-lg p-4">
                    <div className="text-center mb-3">
                      <div className="text-lg font-bold text-green-900">Variant 1 Timeline</div>
                      <div className="text-3xl font-bold text-green-600">~6 months</div>
                    </div>
                    <div className="space-y-2 text-sm text-green-800 font-semibold">
                      <div>📅 Month 1-3: Champions operate, community learns</div>
                      <div>🗳️ Month 4: Candidate registration (4 weeks)</div>
                      <div>📢 Month 5: Campaigning (4 weeks)</div>
                      <div>🎤 Month 6 Week 1: Primary vote (if needed)</div>
                      <div>💬 Month 6 Week 2: Debate event</div>
                      <div>✅ Month 6 Week 3: Final election</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg mb-2">
                        Ideal Scenario: Well-Staffed Launch
                      </h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        We recruit 15-25 champions who fill key governance positions before launch.
                      </p>
                    </div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className="bg-green-100 text-green-800 border-green-300"
                      >
                        Pre-Launch
                      </Badge>
                      <span className="text-sm font-semibold">6 Key Departments Staffed</span>
                    </div>
                    <ul className="space-y-1 text-sm text-muted-foreground ml-4">
                      <li>• Community SubPanel: 1 Community Elect + 3 Community Directors</li>
                      <li>• Finance: 1 Department Elect + 1-2 Department Directors</li>
                      <li>• Governance & Voting: 1 Department Elect + 1-2 Department Directors</li>
                      <li>• Dispute Resolution: 1 Department Elect + 1 Department Director</li>
                      <li>• Group Purchasing: 1 Department Elect + 1 Department Director</li>
                      <li>• Community Engagement: 1 Department Elect + 1 Department Director</li>
                      <li>
                        • Transition & Succession: 1 Department Elect + 3 Department Directors
                        minimum (scales to 2 complete transition SubPanels per 9 departments; can
                        absorb multiple simultaneous SubPanel removals via Nuclear Option)
                      </li>
                    </ul>
                    <p className="text-xs text-green-800 italic">
                      <strong>~24-29 champions in leadership positions on Day 1</strong>
                    </p>
                    <div className="mt-3 bg-cyan-50 border border-cyan-200 rounded p-2">
                      <p className="text-xs text-cyan-800">
                        <strong>Transition Department Scaling:</strong> With 10 departments (40
                        active officials), maintain{' '}
                        <strong>
                          minimum 8 people (20% reserve capacity = 2 transition SubPanels)
                        </strong>
                        . Ideal: 12 people (30% coverage = 3 transition SubPanels) to buffer against
                        turnover during initial elections. These elected observers serve as
                        reserves—bi-weekly check-ins, minimal time commitment—until activated for
                        succession. If residents remove multiple SubPanels simultaneously via
                        Nuclear Option, transition team members with relevant expertise step in as
                        Acting officials within 24 hours, maintaining continuity during the 90+ day
                        emergency election.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-purple-600" />
                      <h5 className="font-semibold">
                        Timeline: 3-Month Stabilization + Election Process
                      </h5>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="p-3">
                          <div className="text-base font-semibold text-blue-900 mb-1">
                            Months 1-3: Community Learns the System
                          </div>
                          <p className="text-base text-blue-800">
                            Champions operate governance, residents learn how the platform works,
                            community gets familiar with departments, roles, and how decisions are
                            made. This context is critical for informed voting later.
                          </p>
                        </CardContent>
                      </Card>

                      <Card className="bg-purple-50 border-purple-200">
                        <CardContent className="p-3">
                          <div className="text-base font-semibold text-purple-900 mb-1">
                            Month 4: Candidate Registration Period (4 Weeks)
                          </div>
                          <p className="text-base text-purple-800">
                            Elections open. Any resident can register as a candidate for any
                            position. Full month (4 weeks) gives residents time to carefully
                            consider running and evaluate the roles. No rushing this important
                            decision.
                          </p>
                        </CardContent>
                      </Card>

                      <Card className="bg-indigo-50 border-indigo-200">
                        <CardContent className="p-3">
                          <div className="text-base font-semibold text-indigo-900 mb-1">
                            Month 5: Active Campaign Period (4 Weeks)
                          </div>
                          <p className="text-base text-indigo-800">
                            Registration closes. <strong>ALL candidates campaign.</strong> Every
                            person running for a position gets a full month to share their vision,
                            attend community events, engage with residents, and build support.
                            Whether there are 2 candidates or 10 per role, everyone campaigns
                            equally.
                          </p>
                        </CardContent>
                      </Card>

                      <Card className="bg-pink-50 border-pink-200">
                        <CardContent className="p-3">
                          <div className="text-base font-semibold text-pink-900 mb-1">
                            Month 6 (Week 1): Preliminary Vote (If Needed)
                          </div>
                          <p className="text-base text-pink-800">
                            <strong>After campaigning ends:</strong> If 4+ candidates per position,
                            community votes to narrow field to top 3 finalists. If 3 or fewer
                            candidates, skip this step and move directly to debate week.
                          </p>
                        </CardContent>
                      </Card>

                      <Card className="bg-amber-50 border-amber-200">
                        <CardContent className="p-3">
                          <div className="text-base font-semibold text-amber-900 mb-1">
                            Month 6 (Week 2): Community Debate Event
                          </div>
                          <p className="text-base text-amber-800">
                            <strong>Live debate stage:</strong> Top 3 finalists (or all candidates
                            if ≤3) debate publicly at a local venue, potentially broadcast on local
                            TV. Residents see candidates side-by-side, ask questions, and compare
                            leadership styles before voting.
                          </p>
                        </CardContent>
                      </Card>

                      <Card className="bg-green-50 border-green-200">
                        <CardContent className="p-3">
                          <div className="text-base font-semibold text-green-900 mb-1">
                            Month 6 (Week 3): Final Election Vote
                          </div>
                          <p className="text-base text-green-800">
                            <strong>Community decides:</strong> Residents vote for their preferred
                            candidates (from top 3 finalists). Winners take office. Champions
                            transition out (unless they ran and won). {cityName} governance is now
                            fully elected and community-controlled.
                          </p>
                        </CardContent>
                      </Card>
                    </div>

                    <div className="bg-blue-100 border-l-4 border-l-blue-600 p-3 mt-3">
                      <p className="text-base text-blue-900">
                        <strong>
                          Total Timeline: ~6 months from launch to fully elected governance.
                        </strong>{' '}
                        The 3-month champion period + 3-month election process ensures residents
                        vote with full context and candidates have time to genuinely campaign.
                      </p>
                    </div>
                  </div>

                  <div className="bg-blue-50 border-l-4 border-l-blue-500 p-4">
                    <p className="text-sm text-blue-900">
                      <strong>Why 3 months + election process?</strong> It gives the community
                      enough time to understand how the platform works, see champions in action, and
                      make informed decisions about who should lead long-term. The additional
                      election timeline (registration, campaigning, debates, voting) mirrors
                      real-world democratic processes and builds genuine community engagement.
                    </p>
                  </div>

                  {/* 2-Month Handoff Period */}
                  <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50 mt-4">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Users className="w-5 h-5 text-purple-600" />
                        Required 2-Month Handoff Period
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="bg-white border border-purple-200 rounded-lg p-3">
                        <p className="text-sm sm:text-base text-gray-800 mb-2">
                          <strong>Community Requirement:</strong> When elections complete and new
                          leaders take office,{' '}
                          <strong>
                            outgoing champions/electors must provide a 2-month knowledge transfer
                            window
                          </strong>{' '}
                          to incoming leaders.
                        </p>
                        <p className="text-sm sm:text-base text-gray-700">
                          This applies to anyone who held a temporary position during the initial 6
                          months (champions) <strong>or</strong> anyone who loses re-election in
                          future cycles.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">
                            ✓
                          </div>
                          <p className="text-sm text-gray-700">
                            <strong>What's included:</strong> Sharing existing systems,
                            documentation, vendor contacts, ongoing projects, institutional
                            knowledge, and operational processes
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">
                            ✓
                          </div>
                          <p className="text-sm text-gray-700">
                            <strong>Flexible duration:</strong> New elector can release outgoing
                            person earlier if handoff completes sooner (could be 2 weeks if transfer
                            is smooth)
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">
                            ✓
                          </div>
                          <p className="text-sm text-gray-700">
                            <strong>Buffer protection:</strong> 2-month maximum ensures new leaders
                            aren't arriving to an abandoned position. proper knowledge transfer is
                            part of civil service etiquette
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">
                            ✓
                          </div>
                          <p className="text-sm text-gray-700">
                            <strong>Applies to all future elections:</strong> This handoff
                            requirement continues for every election cycle. it's a permanent
                            community governance standard
                          </p>
                        </div>
                      </div>

                      <div className="bg-purple-100 border-l-4 border-l-purple-600 p-3 rounded">
                        <p className="text-sm text-purple-900">
                          <strong>Why this matters:</strong> Continuity of governance. No leader
                          should start from zero. Whether you were a champion or an elected
                          official, if you lose your position, you help the next person succeed.
                          This builds institutional memory and prevents knowledge loss.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Variant 2: Minimal Champions */}
                <TabsContent value="minimal" className="space-y-4 mt-6">
                  {/* Variant 2 Timeline Summary Card */}
                  <Card className="bg-gradient-to-br from-amber-100 to-amber-50 border-2 border-amber-400 shadow-sm">
                    <CardContent className="p-6">
                      <div className="text-center mb-4">
                        <h4 className="text-xl font-bold text-amber-900 mb-2">
                          🏃‍♂️ Variant 2 Timeline
                        </h4>
                        <p className="text-4xl font-extrabold text-amber-700">~2.5 Months</p>
                      </div>
                      <div className="space-y-2 text-sm text-amber-800 font-semibold">
                        <p>
                          <strong>Day 1:</strong> 🚀 Launch with skeleton crew
                        </p>
                        <p>
                          <strong>Week 1-4:</strong> 📢 Emergency recruitment push
                        </p>
                        <p>
                          <strong>Week 5-8:</strong> 🗳️ Community-led nominations & campaigns
                        </p>
                        <p>
                          <strong>Week 9:</strong> 🎯 Quick onboarding & training
                        </p>
                        <p>
                          <strong>Week 10:</strong> ⚡ Fast-track election week
                        </p>
                        <p>
                          <strong>Week 11:</strong> 🎉 Full system operational
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Zap className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-bold text-lg mb-2">
                        Minimal Scenario: Fast-Track Elections
                      </h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        We only find 4-8 champions, meaning most departments are unstaffed at
                        launch.
                      </p>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className="bg-amber-100 text-amber-800 border-amber-300"
                      >
                        Pre-Launch
                      </Badge>
                      <span className="text-sm font-semibold">Bare Minimum Coverage</span>
                    </div>
                    <ul className="space-y-1 text-sm text-muted-foreground ml-4">
                      <li>• Community SubPanel: 1 Community Elect + 1-2 Community Directors</li>
                      <li>• Finance: 1 Department Elect + 1 Department Director</li>
                      <li>• Governance & Voting: 1 Department Elect + 1 Department Director</li>
                      <li>
                        • <strong>Most other departments empty</strong>
                      </li>
                    </ul>
                    <p className="text-xs text-amber-800 italic">
                      <strong>~5-7 champions in leadership positions on Day 1</strong>
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-amber-600" />
                      <h5 className="font-semibold">Timeline: Community-Driven from Day 1</h5>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="p-3">
                          <div className="text-base font-semibold text-blue-900 mb-1">
                            Weeks 1-4: Candidate Registration Period (4 Weeks)
                          </div>
                          <p className="text-base text-blue-800">
                            <strong>Day 1 Launch:</strong> Elections open immediately. Any resident
                            can register as a candidate for any open position (Community Elect,
                            Community Directors, Department Elect, Department Directors). Full month
                            (4 weeks) gives residents time to carefully consider running and
                            evaluate available roles. No rushing.
                          </p>
                        </CardContent>
                      </Card>

                      <Card className="bg-purple-50 border-purple-200">
                        <CardContent className="p-3">
                          <div className="text-base font-semibold text-purple-900 mb-1">
                            Weeks 5-8: Active Campaign Period (4 Weeks)
                          </div>
                          <p className="text-base text-purple-800">
                            Registration closes. <strong>ALL candidates campaign.</strong> Every
                            person running gets a full month to introduce themselves, share their
                            vision, engage with residents, and gather support. Whether 2 candidates
                            or 10, everyone campaigns equally.
                          </p>
                        </CardContent>
                      </Card>

                      <Card className="bg-indigo-50 border-indigo-200">
                        <CardContent className="p-3">
                          <div className="text-base font-semibold text-indigo-900 mb-1">
                            Week 9: Preliminary Vote (If Needed)
                          </div>
                          <p className="text-base text-indigo-800">
                            <strong>After campaigning ends:</strong> If 4+ candidates per position,
                            community votes to narrow field to top 3 finalists based on popular
                            vote. If 3 or fewer candidates, skip this step and move directly to
                            debate week.
                          </p>
                        </CardContent>
                      </Card>

                      <Card className="bg-pink-50 border-pink-200">
                        <CardContent className="p-3">
                          <div className="text-base font-semibold text-pink-900 mb-1">
                            Week 10: Community Debate Event
                          </div>
                          <p className="text-base text-pink-800">
                            <strong>Live debate stage:</strong> Top 3 finalists (or all candidates
                            if ≤3) debate publicly at a local venue. Could be broadcast on local
                            news. Residents get to see candidates side-by-side, ask questions, and
                            compare leadership styles before voting.
                          </p>
                        </CardContent>
                      </Card>

                      <Card className="bg-green-50 border-green-200">
                        <CardContent className="p-3">
                          <div className="text-base font-semibold text-green-900 mb-1">
                            Week 11: Final Election Vote
                          </div>
                          <p className="text-base text-green-800">
                            <strong>Community decides:</strong> Residents vote for their preferred
                            candidates (from top 3 finalists). Winners take office. New leaders
                            transition in while champions step down (or run for re-election if they
                            won). Community governance is now fully elected.
                          </p>
                        </CardContent>
                      </Card>
                    </div>

                    <div className="bg-amber-100 border-l-4 border-l-amber-600 p-3 mt-3">
                      <p className="text-base text-amber-900">
                        <strong>
                          Total Timeline: ~11 weeks (~2.5 months) from launch to elected governance.
                        </strong>{' '}
                        This gives ALL candidates time to campaign first, then the community filters
                        to finalists via preliminary vote, followed by debates and final election.
                      </p>
                    </div>
                  </div>

                  <div className="bg-green-50 border-l-4 border-l-green-500 p-4">
                    <p className="text-sm text-green-900">
                      <strong>Why This Works:</strong> Immediate elections are more democratic and
                      inviting. anyone can run on Day 1! While residents don't have months of
                      champion demonstrations, they do get 3-4 weeks of active campaigning and
                      potentially live debates to evaluate candidates. This path is perfect if the
                      community is highly engaged and ready to take ownership immediately.
                    </p>
                  </div>

                  {/* 2-Month Handoff Period - Variant 2 */}
                  <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50 mt-4">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Users className="w-5 h-5 text-purple-600" />
                        Required 2-Month Handoff Period
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="bg-white border border-purple-200 rounded-lg p-3">
                        <p className="text-sm sm:text-base text-gray-800 mb-2">
                          <strong>Community Requirement:</strong> When elections complete and new
                          leaders take office,{' '}
                          <strong>
                            outgoing champions/electors must provide a 2-month knowledge transfer
                            window
                          </strong>{' '}
                          to incoming leaders.
                        </p>
                        <p className="text-sm sm:text-base text-gray-700">
                          Even in this fast-track scenario, anyone who held a position during the
                          initial weeks (champions) must assist incoming elected leaders with proper
                          handoff.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">
                            ✓
                          </div>
                          <p className="text-sm text-gray-700">
                            <strong>What's included:</strong> Sharing existing systems,
                            documentation, vendor contacts, ongoing projects, institutional
                            knowledge, and operational processes
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">
                            ✓
                          </div>
                          <p className="text-sm text-gray-700">
                            <strong>Flexible duration:</strong> New elector can release outgoing
                            person earlier if handoff completes sooner (could be 2 weeks if transfer
                            is smooth)
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">
                            ✓
                          </div>
                          <p className="text-sm text-gray-700">
                            <strong>Buffer protection:</strong> 2-month maximum ensures new leaders
                            aren't arriving to an abandoned position. proper knowledge transfer is
                            part of civil service etiquette
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-5 h-5 rounded-full bg-purple-100 text-purple-700 text-xs flex items-center justify-center font-bold flex-shrink-0 mt-0.5">
                            ✓
                          </div>
                          <p className="text-sm text-gray-700">
                            <strong>Applies to all future elections:</strong> This handoff
                            requirement continues for every election cycle. it's a permanent
                            community governance standard
                          </p>
                        </div>
                      </div>

                      <div className="bg-purple-100 border-l-4 border-l-purple-600 p-3 rounded">
                        <p className="text-sm text-purple-900">
                          <strong>Why this matters:</strong> Continuity of governance. No leader
                          should start from zero. Whether you were a champion or an elected
                          official, if you lose your position, you help the next person succeed.
                          This builds institutional memory and prevents knowledge loss.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </CollapsibleSection>

            {/* Community Debate Event - New Tradition */}
            <CollapsibleSection
              title={
                <>
                  <Users className="w-6 h-6 text-pink-600" />
                  The Community Debate: A New Democratic Tradition
                </>
              }
              subtitle={
                <span>
                  Making governance transparent, engaging, and community-driven through live
                  candidate debates
                </span>
              }
              className="border-2 border-pink-200 bg-gradient-to-br from-pink-50 to-purple-50"
            >
              <div className="space-y-4">
                <div className="bg-pink-100 border-2 border-pink-300 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Zap className="w-6 h-6 text-pink-700 mt-1 flex-shrink-0" />
                    <div className="space-y-2">
                      <h4 className="font-bold text-pink-900 text-base">
                        Imagine This: Election Week in {cityName}
                      </h4>
                      <p className="text-sm text-pink-800 leading-relaxed">
                        It's the week before the final vote. A local venue is set up with a debate
                        stage. <strong>90 candidates</strong> are running for{' '}
                        <strong>30 positions</strong> across the Community SubPanel and 6-9 key
                        departments. The top 3 finalists for each position take the stage in
                        rotation throughout the evening.
                      </p>
                      <p className="text-sm text-pink-800 leading-relaxed">
                        <strong>Local news covers it live.</strong> Residents attend in person or
                        watch from home. It's not stuffy or serious like national elections. it's
                        engaging, informative, and even fun. Candidates introduce themselves,
                        explain their vision, answer community questions, and debate their
                        competitors. By the end of the night, residents know exactly who they want
                        to lead.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-lg">How Community Debates Work</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="bg-white border-pink-200">
                      <CardHeader>
                        <CardTitle className="text-lg">Who Participates?</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <p className="text-base text-muted-foreground">
                          <strong>Only top 3 finalists</strong> per position participate in the
                          debate (determined by preliminary voting if more than 3 candidates
                          registered).
                        </p>
                        <div className="bg-pink-50 rounded p-2">
                          <p className="text-base text-pink-800">
                            <strong>Example:</strong> 3 candidates for Community Elect, 9 candidates
                            for the 3 Community Director roles (3 per slot), 3 candidates for
                            Finance Department Elect, etc.
                          </p>
                        </div>
                        <p className="text-base text-muted-foreground italic">
                          If a position only has 1-2 candidates, they still participate to introduce
                          themselves to the community.
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="bg-white border-purple-200">
                      <CardHeader>
                        <CardTitle className="text-lg">What Happens During the Debate?</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <ul className="space-y-2 text-base text-muted-foreground">
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-3 h-3 text-purple-600 mt-0.5 flex-shrink-0" />
                            <span>
                              <strong>Opening statements:</strong> Candidates introduce themselves
                              and their vision (2-3 minutes each)
                            </span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-3 h-3 text-purple-600 mt-0.5 flex-shrink-0" />
                            <span>
                              <strong>Moderated questions:</strong> Questions about their
                              qualifications, priorities, and decision-making approach
                            </span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-3 h-3 text-purple-600 mt-0.5 flex-shrink-0" />
                            <span>
                              <strong>Community Q&A:</strong> Residents submit questions live
                              (in-person or digitally)
                            </span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-3 h-3 text-purple-600 mt-0.5 flex-shrink-0" />
                            <span>
                              <strong>Closing remarks:</strong> Final pitch to the community on why
                              they should vote for them
                            </span>
                          </li>
                        </ul>
                      </CardContent>
                    </Card>

                    <Card className="bg-white border-indigo-200">
                      <CardHeader>
                        <CardTitle className="text-lg">Format & Timing</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <p className="text-base text-muted-foreground">
                          <strong>Debate rotation:</strong> Positions cycle through in 20-30 minute
                          blocks. Community Elect debates first (highest profile), then Community
                          Directors, then Department Elects and Department Directors.
                        </p>
                        <div className="bg-indigo-50 rounded p-2">
                          <p className="text-base text-indigo-800">
                            <strong>2-Day Event Option:</strong> Day 1 covers Community SubPanel and
                            top 3 departments. Day 2 covers remaining departments. Prevents marathon
                            sessions.
                          </p>
                        </div>
                        <p className="text-base text-muted-foreground italic">
                          Total event time: 3-4 hours for single-day format, 2-3 hours per day for
                          two-day format.
                        </p>
                      </CardContent>
                    </Card>

                    <Card className="bg-white border-green-200">
                      <CardHeader>
                        <CardTitle className="text-lg">Why This Matters</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <ul className="space-y-2 text-base text-muted-foreground">
                          <li className="flex items-start gap-2">
                            <TrendingUp className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                            <span>
                              <strong>Transparency:</strong> Residents see candidates think on their
                              feet, handle pressure, and articulate their vision publicly
                            </span>
                          </li>
                          <li className="flex items-start gap-2">
                            <TrendingUp className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                            <span>
                              <strong>Engagement:</strong> Makes voting exciting and communal, not
                              just a checkbox
                            </span>
                          </li>
                          <li className="flex items-start gap-2">
                            <TrendingUp className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                            <span>
                              <strong>Media coverage:</strong> Local news loves this. It's engaging
                              content that drives viewership and civic pride
                            </span>
                          </li>
                          <li className="flex items-start gap-2">
                            <TrendingUp className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                            <span>
                              <strong>Informed voting:</strong> Residents vote with full context,
                              not just names on a ballot
                            </span>
                          </li>
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-pink-50 to-purple-50 border-l-4 border-l-pink-500 p-4 space-y-2">
                  <h4 className="font-bold text-pink-900 text-lg flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Standardized & Fair Debate Framework
                  </h4>
                  <p className="text-base text-pink-800 leading-relaxed">
                    We'll create a <strong>standardized debate game framework</strong> that's fair,
                    engaging, and replicable across all communities. Rules for speaking time,
                    question allocation, and moderation will be transparent and consistent. This
                    isn't partisan. it's about showcasing leadership, vision, and competence in a
                    structured, equal-opportunity format.
                  </p>
                  <p className="text-base text-pink-800 leading-relaxed">
                    <strong>Optional but encouraged:</strong> Communities can choose whether to host
                    debate events, but we'll provide all the infrastructure, guidelines, and tools
                    to make it easy and impactful.
                  </p>
                </div>

                <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-amber-900 leading-relaxed">
                        <strong>Timeline Integration:</strong> The debate happens in the{' '}
                        <strong>final week before voting</strong> (Week 8 for Minimal Champions,
                        Month 5 Week 5 for Sufficient Champions). This ensures candidates have
                        finished campaigning and are ready to make their final case. The vote opens{' '}
                        <strong>one week after the debate</strong> while impressions are still
                        fresh.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Election Process Details */}
            <CollapsibleSection
              title={
                <>
                  <Vote className="w-6 h-6 text-blue-600" />
                  How Elections Work (3-Stage Process)
                </>
              }
              subtitle={
                <span>
                  Whether elections happen in Month 4 (Variant 1) or Day 1 (Variant 2), the process
                  is the same for every position.
                </span>
              }
              className="border-2 border-blue-200 bg-blue-50/50"
              defaultOpen={false}
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Stage 1 */}
                  <Card className="bg-white border-blue-200">
                    <CardHeader>
                      <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">
                          1
                        </div>
                        Candidate Registration
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <p className="text-sm sm:text-base text-muted-foreground">
                        <strong>Open to everyone.</strong> Any resident can declare candidacy for
                        any open position (Community Elect, Community Directors, Department Elect,
                        Department Directors).
                      </p>
                      <div className="bg-blue-50 rounded p-2">
                        <p className="text-sm sm:text-base text-blue-800">
                          <strong>Example:</strong> 20 residents sign up to run for Community Elect.
                          15 residents sign up for Finance Director.
                        </p>
                      </div>
                      <p className="text-sm text-muted-foreground italic">
                        Vetting process may require minimum support signatures (like state
                        elections).
                      </p>
                    </CardContent>
                  </Card>

                  {/* Stage 2 */}
                  <Card className="bg-white border-purple-200">
                    <CardHeader>
                      <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-xs font-bold">
                          2
                        </div>
                        Primary Vote (Top 3)
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <p className="text-sm sm:text-base text-muted-foreground">
                        <strong>Narrow the field.</strong> All residents vote to select the top 3
                        candidates for each position.
                      </p>
                      <div className="bg-purple-50 rounded p-2">
                        <p className="text-sm sm:text-base text-purple-800">
                          <strong>Example:</strong> Out of 20 Community Elect candidates, the 3 with
                          the most votes advance to the final round.
                        </p>
                      </div>
                      <p className="text-sm text-muted-foreground italic">
                        This prevents ballot overload and ensures finalists have broad support.
                      </p>
                    </CardContent>
                  </Card>

                  {/* Stage 3 */}
                  <Card className="bg-white border-green-200">
                    <CardHeader>
                      <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold">
                          3
                        </div>
                        Final Election
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <p className="text-sm sm:text-base text-muted-foreground">
                        <strong>Winner takes office.</strong> The 3 finalists compete in a final
                        popular vote. Whoever gets the most votes wins the position.
                      </p>
                      <div className="bg-green-50 rounded p-2">
                        <p className="text-sm sm:text-base text-green-800">
                          <strong>Example:</strong> Out of the top 3 Community Elect candidates,
                          Candidate B wins with 52% of the vote.
                        </p>
                      </div>
                      <p className="text-sm text-muted-foreground italic">
                        Elected leaders serve <strong>2-year terms</strong>. All positions
                        (Community Elect, Community Directors, Department Elect, Department
                        Directors) follow the same term length to maintain synchronized election
                        cycles.
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <UserCheck className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="space-y-2">
                      <p className="text-base sm:text-lg font-semibold text-blue-900">
                        This Process Applies to Every Position
                      </p>
                      <p className="text-sm sm:text-base text-blue-800 leading-relaxed">
                        Whether it's the Community Elect, a Community Director, a Department Elect,
                        or a Department Director, everyone goes through the same democratic process.
                        No appointments. No backroom deals. Just transparent elections with a tally
                        anyone can recount.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* One Community, No Parties - Non-Partisan Requirement */}
            <CollapsibleSection
              title={
                <>
                  <Users className="w-6 h-6 text-purple-600" />
                  One Community, No Parties: The {cityName} Identity
                </>
              }
              subtitle={
                <span>Why {cityName} community governance is intentionally non-partisan</span>
              }
              className="border-2 border-red-300 bg-gradient-to-br from-red-50 via-white to-blue-50"
            >
              <div className="space-y-4">
                <div className="bg-purple-100 border-2 border-purple-300 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Shield className="w-6 h-6 text-purple-700 mt-1 flex-shrink-0" />
                    <div className="space-y-3">
                      <h4 className="font-bold text-purple-900 text-base sm:text-lg">
                        We Are One {cityName}
                      </h4>
                      <p className="text-sm sm:text-base text-purple-900 leading-relaxed">
                        There is only <strong>one {cityName}</strong>. Not a Republican {cityName}.
                        Not a Democratic {cityName}. Just <strong>{cityName}</strong>. a single body
                        of residents working together to govern their community.
                      </p>
                      <p className="text-sm sm:text-base text-purple-800 leading-relaxed">
                        When you run for office in the {cityName} Community App,{' '}
                        <strong>you represent the people of {cityName}</strong>. not a political
                        party trying to embed itself in our community. This rule is non-negotiable
                        and is built into our candidate registration process.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white border-2 border-gray-300 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 text-base sm:text-lg mb-3">
                    Candidate Registration Requirement
                  </h4>
                  <div className="space-y-3">
                    <div className="bg-red-50 border-l-4 border-red-600 p-3 rounded">
                      <p className="text-sm sm:text-base text-red-900 font-semibold mb-2">
                        ⚠️ Automatic Disqualification Policy
                      </p>
                      <p className="text-sm text-red-800 leading-relaxed">
                        Candidates who campaign, debate, or register as representatives of a{' '}
                        <strong>national political party (Republican, Democrat, etc.)</strong> will
                        be <strong>automatically disqualified</strong> from the race.
                      </p>
                    </div>

                    <div className="space-y-2 text-sm text-gray-700">
                      <div className="flex items-start gap-2">
                        <span className="text-red-600 font-bold">✗</span>
                        <p>
                          <strong>NOT ALLOWED:</strong> "Vote for me, I'm the Republican candidate
                          for {cityName} Community Elect"
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-red-600 font-bold">✗</span>
                        <p>
                          <strong>NOT ALLOWED:</strong> Accepting campaign donations from national
                          party organizations
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-red-600 font-bold">✗</span>
                        <p>
                          <strong>NOT ALLOWED:</strong> Using party affiliation as debate leverage
                          or campaign identity
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-green-600 font-bold">✓</span>
                        <p>
                          <strong>ALLOWED:</strong> "I'm running to represent {cityName} residents
                          and here are my ideas for our community"
                        </p>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-green-600 font-bold">✓</span>
                        <p>
                          <strong>ALLOWED:</strong> Discussing your viewpoints, approaches, and
                          solutions to community issues
                        </p>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-3">
                      <p className="text-xs sm:text-sm text-blue-900">
                        <strong>Note:</strong> Mentioning parties in conversation or historical
                        context is fine. The rule is specifically about{' '}
                        <strong>identifying as a party candidate</strong> or using party affiliation
                        as your campaign platform. You can have personal beliefs. you just can't run
                        as "Team Red" or "Team Blue" for community governance.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg p-4">
                  <h4 className="font-bold text-amber-900 text-base sm:text-lg mb-3">
                    Why We Leave Party Labels at the Door
                  </h4>
                  <div className="space-y-2 text-sm sm:text-base text-amber-900">
                    <p className="leading-relaxed">
                      <strong>1. Unity Over Division:</strong> We stop calling one team Blue and
                      another team Red. It's one team. {cityName}. This forces us to work together
                      and solve problems as neighbors, not political opponents.
                    </p>
                    <p className="leading-relaxed">
                      <strong>2. Fresh Start:</strong> The party system creates the same
                      frustrations we see at state and federal levels. By removing party labels, we
                      don't recreate those frameworks. we lead by example.
                    </p>
                    <p className="leading-relaxed">
                      <strong>3. Ideas Over Loyalty:</strong> Vote for the person with the best
                      ideas for {cityName}, not because they wear your team's colors. Community
                      governance should be about solutions, not party talking points.
                    </p>
                    <p className="leading-relaxed">
                      <strong>4. Local Ecosystem:</strong> {cityName} runs itself. We don't need
                      national party donations or influence. This is community governance by{' '}
                      {cityName} residents, for {cityName} residents.
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 text-base sm:text-lg mb-3 flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Historical Context: Our Founding Fathers' Warning
                  </h4>
                  <div className="space-y-3">
                    <div className="bg-white border-l-4 border-l-indigo-600 p-3 rounded">
                      <p className="text-sm sm:text-base text-gray-800 italic leading-relaxed mb-2">
                        "The alternate domination of one faction over another, sharpened by the
                        spirit of revenge... is itself a frightful despotism."
                      </p>
                      <p className="text-xs sm:text-sm text-gray-600">
                        . George Washington, Farewell Address (1796)
                      </p>
                    </div>

                    <div className="space-y-2 text-sm text-gray-700">
                      <p className="leading-relaxed">
                        <strong>George Washington was an Independent.</strong> Many of our Founding
                        Fathers were independents. Washington explicitly warned against the
                        formation of political parties, stating they would divide the nation and
                        prioritize party power over the common good.
                      </p>
                      <p className="leading-relaxed">
                        The party system as we know it today{' '}
                        <strong>is not part of America's founding principles:</strong>
                      </p>
                      <ul className="space-y-1 ml-4">
                        <li>
                          • <strong>Democratic Party:</strong> Formed in 1828 (52 years after
                          independence)
                        </li>
                        <li>
                          • <strong>Republican Party:</strong> Formed March 20, 1854 (78 years after
                          independence)
                        </li>
                      </ul>
                      <p className="leading-relaxed mt-2">
                        <strong>
                          {cityName} is adopting our Founding Fathers' original vision:
                        </strong>{' '}
                        governance by <em>people</em>, not by party machinery. We are the People's
                        Community of {cityName}.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-300 rounded-lg p-4">
                  <h4 className="font-bold text-green-900 text-base mb-2">
                    Your Rights at City, County, State, and Federal Levels
                  </h4>
                  <p className="text-sm text-green-800 leading-relaxed">
                    <strong>This rule only applies to {cityName} Community App governance.</strong>{' '}
                    You still have every right as a U.S. citizen to vote for any party you choose in
                    city, county, state, and federal elections. Your national voting rights remain
                    completely intact. We're simply saying that{' '}
                    <strong>within our local community governance system</strong>, we operate as one
                    unified community without party labels.
                  </p>
                </div>

                <div className="bg-gradient-to-r from-blue-100 to-purple-100 border-2 border-blue-300 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <TrendingUp className="w-6 h-6 text-blue-700 mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-blue-900 text-base sm:text-lg mb-2">
                        {cityName} Leads by Example
                      </h4>
                      <p className="text-sm sm:text-base text-blue-800 leading-relaxed">
                        We have a shot to demonstrate what happens when a community{' '}
                        <strong>stops blaming "the Democrats" or "the Republicans"</strong> and
                        starts working together as <em>{cityName} residents</em>. When we remove
                        party labels, we get down to the root of what matters:{' '}
                        <strong>solving problems together as one team.</strong>
                      </p>
                      <p className="text-sm sm:text-base text-blue-800 leading-relaxed mt-2">
                        If {cityName} succeeds at this, we inspire other communities nationwide to
                        do the same. That's how real change starts. one community at a time.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* National Election Day - November 3rd */}
            <CollapsibleSection
              title={
                <>
                  <Calendar className="w-6 h-6 text-indigo-600" />
                  National Community Governance Day: November 3rd
                </>
              }
              subtitle={
                <span>Building a nationwide movement through synchronized election cycles</span>
              }
              className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50"
            >
              <div className="space-y-4">
                <div className="bg-indigo-100 border-2 border-indigo-300 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <TrendingUp className="w-6 h-6 text-indigo-700 mt-1 flex-shrink-0" />
                    <div className="space-y-2">
                      <h4 className="font-bold text-indigo-900 text-base">
                        The Vision: A National Network Effect
                      </h4>
                      <p className="text-sm text-indigo-800 leading-relaxed">
                        Imagine a future where{' '}
                        <strong>
                          every community using our platform across the entire nation holds their
                          elections on the same day
                        </strong>
                        . November 3rd. From {cityName} to cities across Louisiana, Texas, and
                        eventually all 50 states, millions of residents participate in community
                        governance elections simultaneously.
                      </p>
                      <p className="text-sm text-indigo-800 leading-relaxed">
                        This isn't just about elections. it's about creating{' '}
                        <strong>national civic pride at the community level</strong>. November 3rd
                        becomes the day Americans take back control of their local communities.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-lg">How Election Timing Works</h4>

                  <Card className="bg-white border-indigo-200">
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-start gap-2">
                        <div className="w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                          1
                        </div>
                        <div>
                          <h5 className="font-semibold text-sm sm:text-base mb-1">
                            First Election: Whenever Your Community Is Ready
                          </h5>
                          <p className="text-sm sm:text-base text-muted-foreground">
                            Your community's{' '}
                            <strong>first election happens at the earliest convenient date</strong>{' '}
                            after launch (either 3-4 months post-launch for Variant 1, or within 1
                            month for Variant 2). This could be January, March, July. whenever your
                            community completes the deployment process.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <div className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                          2
                        </div>
                        <div>
                          <h5 className="font-semibold text-sm sm:text-base mb-1">
                            Term Length: 2 Years
                          </h5>
                          <p className="text-sm sm:text-base text-muted-foreground">
                            All elected leaders serve <strong>2-year terms</strong>. This applies to
                            the Community Elect, all Directors, and all Department positions. Two
                            years provides stability while ensuring regular accountability through
                            elections.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                          3
                        </div>
                        <div>
                          <h5 className="font-semibold text-sm sm:text-base mb-1">
                            Second Election: November 3rd (Alignment Year)
                          </h5>
                          <p className="text-sm sm:text-base text-muted-foreground">
                            Your community's{' '}
                            <strong>
                              second election will occur on November 3rd within the 2-year window
                            </strong>{' '}
                            from your first election. This aligns your community with the national
                            election cycle.
                          </p>
                          <div className="bg-purple-50 border border-purple-200 rounded p-2 mt-2">
                            <p className="text-sm sm:text-base text-purple-800">
                              <strong>Example 1:</strong> If {cityName} holds its first election in{' '}
                              <strong>January 2026</strong>, the second election would occur on{' '}
                              <strong>November 3, 2027</strong> (22 months later, within the 2-year
                              window).
                            </p>
                          </div>
                          <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
                            <p className="text-sm sm:text-base text-blue-800">
                              <strong>Example 2:</strong> If a community holds its first election in{' '}
                              <strong>September 2026</strong>, the second election would occur on{' '}
                              <strong>November 3, 2027</strong> (14 months later, slightly shorter
                              cycle to align).
                            </p>
                          </div>
                          <p className="text-sm text-muted-foreground italic mt-2">
                            <strong>Note:</strong> The first term may be slightly shorter or longer
                            than 24 months to align with November 3rd, but all subsequent elections
                            occur every 2 years on November 3rd.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <div className="w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                          4
                        </div>
                        <div>
                          <h5 className="font-semibold text-sm sm:text-base mb-1">
                            All Future Elections: November 3rd (Every 2 Years)
                          </h5>
                          <p className="text-sm sm:text-base text-muted-foreground">
                            From the second election onward,{' '}
                            <strong>every election happens on November 3rd</strong>. Your community
                            is now synchronized with the national cycle: November 3, 2027 → November
                            3, 2029 → November 3, 2031, and so on.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-l-4 border-l-indigo-500 p-4 space-y-2">
                  <h4 className="font-bold text-indigo-900 text-base sm:text-lg flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Why November 3rd Creates a Movement
                  </h4>
                  <ul className="space-y-2 text-sm sm:text-base text-indigo-800">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong>Statewide Coordination:</strong> When 7 cities in Louisiana all hold
                        elections on November 3rd, it becomes state news. Media coverage, voter
                        turnout, and civic engagement skyrocket.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong>National Visibility:</strong> As we expand to Texas, Florida, Ohio,
                        and beyond. imagine 50 cities, then 100, then 500 communities all
                        participating on the same day. It becomes national news.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong>Network Effect:</strong> The more communities participate
                        simultaneously, the more powerful the movement becomes. November 3rd
                        transforms into "Community Governance Day". a new American tradition.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong>Voter Awareness:</strong> Residents see their neighbors voting, see
                        news coverage, and feel compelled to participate. Synchronized elections
                        drive higher turnout and engagement.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                      <span>
                        <strong>Civic Pride:</strong> Being part of a nationwide movement is
                        inspiring. {cityName} isn't just voting for its own leaders. it's
                        participating in a democratic revolution happening across America.
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="space-y-2">
                  <h4 className="font-semibold text-base sm:text-lg">
                    {cityName} Example Timeline
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Card className="bg-blue-50 border-blue-300">
                      <CardContent className="p-3">
                        <div className="text-sm sm:text-base font-bold text-blue-900 mb-1">
                          First Election
                        </div>
                        <div className="text-base sm:text-lg font-semibold text-blue-700 mb-1">
                          March 2026
                        </div>
                        <p className="text-sm sm:text-base text-blue-800">
                          {cityName} completes champion phase, holds first election. Leaders elected
                          for ~20-month term.
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="bg-indigo-50 border-indigo-300">
                      <CardContent className="p-3">
                        <div className="text-sm sm:text-base font-bold text-indigo-900 mb-1">
                          Second Election
                        </div>
                        <div className="text-base sm:text-lg font-semibold text-indigo-700 mb-1">
                          November 3, 2027
                        </div>
                        <p className="text-sm sm:text-base text-indigo-800">
                          First synchronized election. {cityName} now aligned with national cycle.
                          Leaders elected for full 2-year term.
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="bg-purple-50 border-purple-300">
                      <CardContent className="p-3">
                        <div className="text-sm sm:text-base font-bold text-purple-900 mb-1">
                          All Future Elections
                        </div>
                        <div className="text-base sm:text-lg font-semibold text-purple-700 mb-1">
                          Nov 3, 2029/2031...
                        </div>
                        <p className="text-sm sm:text-base text-purple-800">
                          {cityName} participates in national Community Governance Day every 2
                          years, alongside hundreds of other communities.
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm sm:text-base text-amber-900 leading-relaxed">
                        <strong>Bottom Line:</strong> Your first election happens when your
                        community is ready. But by your second election, you're part of something
                        bigger. a nationwide movement to restore community governance across
                        America. November 3rd isn't just {cityName} voting. it's the entire nation
                        participating together.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            {/* Why This Matters */}
            <Card className="border-2 border-green-200 bg-green-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <Shield className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                  <div className="space-y-3">
                    <h3 className="font-bold text-green-900 text-lg">Why This Process Matters</h3>
                    <div className="space-y-2 text-sm text-green-800">
                      <p>
                        <strong>1. No Single Point of Failure:</strong> ToolboxLogic doesn't control
                        who leads your community. Champions are temporary. The community decides who
                        stays in power through elections.
                      </p>
                      <p>
                        <strong>2. Transparent Transition:</strong> Everyone knows when elections
                        happen, who's running, and how voting works. No surprises, no hidden
                        agendas.
                      </p>
                      <p>
                        <strong>3. Ownership from Day 1:</strong> Whether you launch with 20
                        champions or 5, the community always has a clear path to taking full
                        control. This isn't a vendor-managed system. it's yours.
                      </p>
                      <p>
                        <strong>4. Champions Can Lose:</strong> Just because someone was a champion
                        during testing doesn't mean they'll win the election. The community might
                        choose different leaders, and that's exactly how it should work.
                      </p>
                      <p>
                        <strong>5. Knowledge Transfer Built-In:</strong> Every outgoing leader
                        (whether champion or elected) provides a 2-month handoff period to incoming
                        leaders. No one starts from zero. continuity and institutional knowledge are
                        preserved through proper transition protocols.
                      </p>
                      <p>
                        <strong>6. Non-Partisan Governance:</strong> {cityName} operates as one
                        unified community, not as competing political parties. By leaving party
                        labels at the door, we focus on ideas and solutions. not team colors. This
                        is governance by {cityName} residents, for {cityName} residents.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}
